import { computed, createApp } from "vue";

import App from "./App.vue";
import router from "./router";
import TWEEN from "@tweenjs/tween.js";
import store from "./store";
import { v4 as uuidv4 } from "uuid"; // Import the uuid library

import AxL from "@/lib/axl";
import EventBus from "@/lib/event_bus";

const DEFAULT_LOCALE = "en";

function setLocale(store, locale) {
  store.dispatch("setLocale", locale);
  store.dispatch("getContent", locale);
}

function setAppMode(store, mode) {
  store.state.appMode = mode;
}

console.log(
  "%c🪸 Calling in our corals 🪸",
  "background: #00222a; color: #ffffff; padding:10px; font-size: 50px;"
);

const APP_MODES = {
  booth: "BOOTH",
  experiment: "experiment",
};

let AXL_MODE = process.env.VUE_APP_AXL_MODE === "prod";
//const APP_MODE = APP_MODES[process.env.VUE_APP_APP_MODE] || 'BOOTH'
AXL_MODE = true;
const AXL_DEV_MODE = true;
const APP_MODE = "experiment";

const bus = new EventBus({ debug: !AXL_MODE }).init({
  events: [
    "PAUSE",
    "RESUME",
    "SHARE",
    "LEAVE",
    "TAG_AUDIO_REQUEST",
    "TAG_AUDIO",
    "VIDEO_START",
    "VIDEO_END",
    "AUDIO_START",
    "AUDIO_END",
    "NEXT_STATE",
    "PREVIOUS_STATE",
    "CHANGE_STATE",
    "DISCONNECT",
    "RECONNECT",
    "TRACK",
  ],
});

// Generate a session_id and store it in the Vuex store
function initializeSession(store) {
  const sessionId = uuidv4();
  store.state.sessionId = sessionId;
  console.log("Generated session_id:", sessionId);
}

async function sendTrackEvent(data) {
  const url = "/calling-in-our-corals/track";

  try {
    const csrfToken = document
      .querySelector('meta[name="csrf-token-corals"]')
      .getAttribute("content");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const responseData = await response.json();
      console.log("Success:", responseData);
    } else {
      const errorData = await response.json();
      console.error("Error:", errorData);
    }
  } catch (error) {
    console.error("Error:", error);
  }
}

const initApp = function () {
  const app = createApp(App).use(store).use(router);

  const urlParams = new URLSearchParams(window.location.search);
  const locale = urlParams.get("lang") || urlParams.get("hl") || DEFAULT_LOCALE;

  //setLocale(store, locale)
  //console.log(APP_MODE)

  // if locale is not supported, default to english
  const supportedLocales = ["en", "ar", "es", "fr", "de"];
  if (!supportedLocales.includes(locale)) {
    setLocale(store, DEFAULT_LOCALE);
  } else {
    setLocale(store, locale);
  }

  //setLocale(store, 'ar')
  setAppMode(store, APP_MODE);
  initializeSession(store); // Initialize the session ID

  app.provide("bus", bus);
  app.mount("#app");

  bus.subscribe({
    event: "TAG_AUDIO",
    callback: (data) => {
      let action = "<location> - tap audio";
      let currentLocation: string =
        store.state.selectedAudioLocation.toString();
      currentLocation = currentLocation.replace("selection.", "");
      action = action.replace("<location>", currentLocation);

      sendTrackEvent({
        file_name: data.trackName,
        time: data.pressedAt,
        session_id: store.state.sessionId,
        date: new Date().toLocaleDateString("en-GB"), // format as dd/MM/yyyy
        interactionType: "tap_audio",
        interaction_sequence: store.state.interactionSequence,
        location: currentLocation,
      });
      store.state.interactionSequence++;
      // new API
      // call the function here
    },
  });

  bus.subscribe({
    event: "TRACK",
    callback: (data) => {
      const action = data.action;
      const currentLocation = store.state.selectedAudioLocation
        .toString()
        .replace("selection.", "");
      const trackEventBase = {
        file_name: data.trackName,
        time: 0,
        session_id: store.state.sessionId,
        date: new Date().toLocaleDateString("en-GB"),
        interaction_sequence: store.state.interactionSequence,
        location: currentLocation,
      };

      if (action === "<location> - play audio") {
        console.log("play audio track");
        console.log("data label is " + data.label);
        sendTrackEvent({
          ...trackEventBase,
          interactionType: "play audio",
          file_name: data.label,
        });
      } else if (action === "<location> - finish audio") {
        sendTrackEvent({
          ...trackEventBase,
          interactionType: "finish audio",
          file_name: data.label,
        });
      }

      if (action.includes("<location>")) {
        const updatedAction = action.replace("<location>", currentLocation);
      } else {
        /*
        axl.sendMessage(AxL.ChildToHost.TRACK, {
          action,
          label: data.label || null,
          value: data.value || null,
        });
        */
      }
      store.state.interactionSequence++;
    },
  });

  const animate = () => {
    TWEEN.update();
    requestAnimationFrame(animate);
  };

  bus.subscribe({
    event: "RESUME",
    callback: () => {
      store.state.paused = false;
      const currentRoute = router.currentRoute.value.name;
      if (currentRoute === "active.about") {
        router.go(-1);
      }
    },
  });

  bus.subscribe({
    event: "PAUSE",
    callback: () => {
      store.state.paused = true;
    },
  });

  animate();
};

const initAxL = function () {
  const axl = new AxL();

  axl.registerMessageCallback(AxL.HostToChild.RESUME, () => {
    bus.publish({ event: "RESUME" });
    console.log("RESUME from Axl");
  });

  axl.registerMessageCallback(AxL.HostToChild.FREEZE, () => {
    bus.publish({ event: "PAUSE" });
    console.log("PAUSE from Axl");
  });

  const gotFirstResume = new Promise<void>((resolve) => {
    axl.registerMessageCallback(AxL.HostToChild.RESUME, () => {
      bus.publish({ event: "RESUME" });
      resolve();
    });
  });

  const gameReady = axl.handshake().then((initialData) => {
    try {
      // TODO: what is objJsonB64 for?
      // TODO: where does Buffer come from?
    } catch (error) {
      console.log("ERROR", error);
    }
  });

  Promise.all([gotFirstResume, gameReady]).then(() => {
    bus.subscribe({
      event: "SHARE",
      callback: (data) => {
        axl.sendMessage(AxL.ChildToHost.SHARE, data);
      },
    });

    bus.subscribe({
      event: "LEAVE",
      callback: ({ url }) => {
        axl.sendMessage(AxL.ChildToHost.NAVIGATE, url);
      },
    });

    bus.subscribe({
      event: "TRACK",
      callback: (data) => {
        // check for <location>
        let action = data.action;
        if (action.includes("<location>")) {
          let currentLocation: string =
            store.state.selectedAudioLocation.toString();
          currentLocation = currentLocation.replace("selection.", "");
          action = action.replace("<location>", currentLocation);
        }

        const obj = {
          action: action,
          label: data.label ? data.label : null,
          value: data.value ? data.value : null,
        };
        console.log("tracing event");
        console.log(obj);

        axl.sendMessage(AxL.ChildToHost.TRACK, obj);
      },
    });

    // TODO: analytics & custom event set up
    bus.subscribe({
      event: "TAG_AUDIO",
      callback: (data) => {
        let action = "<location> - tap audio";
        let currentLocation: string =
          store.state.selectedAudioLocation.toString();
        currentLocation = currentLocation.replace("selection.", "");
        action = action.replace("<location>", currentLocation);

        console.log("track audio event");
        console.log(action, data.trackName, data.pressedAt);

        axl.sendMessage(AxL.ChildToHost.TRACK, {
          action: action,
          label: data.trackName,
          value: data.pressedAt,
        });
      },
    });

    initApp();
  });
};

if (AXL_MODE) {
  initAxL();
  //initApp()
} else {
  initApp();
}
